/* Ekko Branding – Calendly attribution and booking events.
   One lazy-loaded Calendly instance shared across storefront pages.
   Does not fire Meta Lead or GA4 generate_lead directly: connect and verify
   exactly one existing tracking destination before enabling those conversions. */
(() => {
  if (window.ekkoCalendlyInitialized) return;
  window.ekkoCalendlyInitialized = true;

  const DEFAULT_URL = 'https://calendly.com/ekkobranding/30min';
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const KEY = 'ekko_campaign_attribution_v1';
  let popupPending = null;
  let scheduled = false;

  const privacy = () => window.Shopify && window.Shopify.customerPrivacy;
  const analyticsAllowed = () => {
    try { return Boolean(privacy() && privacy().analyticsProcessingAllowed()); }
    catch (_) { return false; }
  };

  function readCurrentUtm() {
    const url = new URL(window.location.href);
    const obj = {};
    for (const key of UTM_KEYS) {
      const value = url.searchParams.get(key);
      if (value && value.length <= 255) obj[key] = value;
    }
    return obj;
  }

  function getUtm() {
    const current = readCurrentUtm();
    let stored = {};
    if (analyticsAllowed()) {
      try {
        stored = JSON.parse(window.sessionStorage.getItem(KEY) || '{}');
      } catch (_) { stored = {}; }
      const combined = Object.assign({}, stored, current);
      try { window.sessionStorage.setItem(KEY, JSON.stringify(combined)); }
      catch (_) {}
      return combined;
    }
    return current;
  }

  function addUtms(url, attributes) {
    const dest = new URL(url);
    for (const key of UTM_KEYS) if (attributes[key]) dest.searchParams.set(key, attributes[key]);
    return dest.href;
  }

  function record(eventName, data) {
    if (!analyticsAllowed()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, data || {}));
    window.dispatchEvent(new CustomEvent('ekko:measurement', { detail: { event: eventName, ...data } }));
  }

  document.addEventListener('visitorConsentCollected', () => {
    if (!analyticsAllowed()) {
      try { window.sessionStorage.removeItem(KEY); } catch (_) {}
    }
  });

  function ensureWidget() {
    if (window.Calendly && window.Calendly.initPopupWidget) return Promise.resolve();
    if (popupPending) return popupPending;
    popupPending = new Promise((resolve, reject) => {
      const cssUrl = 'https://assets.calendly.com/assets/external/widget.css';
      const scriptUrl = 'https://assets.calendly.com/assets/external/widget.js';
      if (!document.querySelector('link[href="' + cssUrl + '"]')) {
        const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = cssUrl; document.head.appendChild(l);
      }
      let s = document.querySelector('script[src="' + scriptUrl + '"]');
      if (!s) {
        s = document.createElement('script'); s.src = scriptUrl; s.async = true;
        s.dataset.ekkoCalendly = '1'; document.head.appendChild(s);
      }
      const deadline = window.setTimeout(() => reject(new Error('Calendly loading timed out')), 12000);
      s.addEventListener('load', () => { window.clearTimeout(deadline); resolve(); }, { once: true });
      s.addEventListener('error', () => { window.clearTimeout(deadline); reject(new Error('Calendly failed to load')); }, { once: true });
    }).finally(() => { popupPending = null; });
    return popupPending;
  }

  window.ekkoOpenCalendly = function (chosenUrl) {
    const target = typeof chosenUrl === 'string' && /^https:\/\/calendly\.com\/ekkobranding\//.test(chosenUrl)
      ? chosenUrl : DEFAULT_URL;
    const utms = getUtm();
    const url = addUtms(target, utms);
    scheduled = false;
    record('ekko_calendly_opened', { booking_source: utms.utm_source || '(unknown)', booking_campaign: utms.utm_campaign || '(none)' });
    return ensureWidget().then(() => {
      if (!window.Calendly || !window.Calendly.initPopupWidget) throw new Error('Calendly not ready');
      window.Calendly.initPopupWidget({ url });
    }).catch(() => { window.location.assign(url); });
  };

  // Capture clicks on direct Calendly links across the entire storefront.
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const a = event.target.closest('a[href]');
    if (!a) return;

    let dest;
    try { dest = new URL(a.href); } catch (_) { return; }

    if (dest.hostname === 'calendly.com' && /^\/ekkobranding\//.test(dest.pathname)) {
      event.preventDefault();
      window.ekkoOpenCalendly(dest.href);
      return;
    }

    // Carry non-personal campaign labels across first-party navigation without cookies.
    // Only touch links on the current domain. Never copy lead identifiers or emails.
    if (dest.origin === window.location.origin && !a.hasAttribute('download')) {
      const campaign = getUtm();
      if (Object.keys(campaign).length) {
        a.href = addUtms(dest.href, campaign);
      }
    }
  }, true);

  // A booking is not a click: it is only counted after the confirmed Calendly iframe event.
  window.addEventListener('message', event => {
    let validOrigin = false;
    try {
      const host = new URL(event.origin).hostname;
      validOrigin = event.origin.startsWith('https://') && (host === 'calendly.com' || host.endsWith('.calendly.com'));
    } catch (_) {}
    if (!validOrigin || !event.data || event.data.event !== 'calendly.event_scheduled') return;
    if (scheduled) return;
    scheduled = true;

    const utms = getUtm();
    const details = {
      conversion_type: 'calendly_booking',
      utm_source: utms.utm_source || '(unknown)',
      utm_medium: utms.utm_medium || '(unknown)',
      utm_campaign: utms.utm_campaign || '(none)',
      utm_content: utms.utm_content || '(none)',
      booking_event_uri: event.data.payload && event.data.payload.event ? String(event.data.payload.event.uri || '') : ''
    };
    record('ekko_calendly_booking_completed', details);
  });
})();
